import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Save, Trash2, User, Building, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function ClientFormPage({ user }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState({
    client_type: 'particulier',
    name: '',
    email: '',
    phone: '',
    address: '',
    postal_code: '',
    city: '',
    country: 'France',
    siret: '',
    tva_number: '',
    notes: ''
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (isEdit) {
      fetchClient();
    }
  }, [id]);

  const fetchClient = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/clients/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData(response.data);
    } catch (error) {
      toast.error('Client non trouvé');
      navigate('/invoices');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('Le nom est obligatoire');
      return;
    }

    setLoading(true);
    try {
      if (isEdit) {
        await axios.put(`${BACKEND_URL}/api/clients/${id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Client mis à jour');
      } else {
        await axios.post(`${BACKEND_URL}/api/clients`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Client créé');
      }
      navigate('/invoices');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer ce client ?')) return;

    setDeleting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/clients/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Client supprimé');
      navigate('/invoices');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/invoices')} variant="outline" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <h1 className="font-heading text-xl font-bold text-slate-900">
              {isEdit ? 'Modifier le client' : 'Nouveau client'}
            </h1>
          </div>
          {isEdit && (
            <Button 
              onClick={handleDelete} 
              variant="outline" 
              size="sm"
              className="text-red-600 border-red-200 hover:bg-red-50"
              disabled={deleting}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 md:p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Type */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Type de client</h2>
            <div className="flex gap-4">
              <label 
                className={`flex-1 flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                  formData.client_type === 'particulier' 
                    ? 'border-slate-900 bg-slate-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <input
                  type="radio"
                  value="particulier"
                  checked={formData.client_type === 'particulier'}
                  onChange={(e) => setFormData({ ...formData, client_type: e.target.value })}
                  className="sr-only"
                />
                <User className="w-6 h-6 text-slate-600" />
                <div>
                  <p className="font-medium text-slate-900">Particulier</p>
                  <p className="text-sm text-slate-500">Client individuel</p>
                </div>
              </label>
              <label 
                className={`flex-1 flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                  formData.client_type === 'professionnel' 
                    ? 'border-slate-900 bg-slate-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <input
                  type="radio"
                  value="professionnel"
                  checked={formData.client_type === 'professionnel'}
                  onChange={(e) => setFormData({ ...formData, client_type: e.target.value })}
                  className="sr-only"
                />
                <Building className="w-6 h-6 text-slate-600" />
                <div>
                  <p className="font-medium text-slate-900">Professionnel</p>
                  <p className="text-sm text-slate-500">Entreprise</p>
                </div>
              </label>
            </div>
          </div>

          {/* Basic Info */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Informations</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="name">
                  {formData.client_type === 'particulier' ? 'Nom complet' : 'Raison sociale'} *
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={formData.client_type === 'particulier' ? 'Jean Dupont' : 'Entreprise SAS'}
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@exemple.com"
                />
              </div>
              <div>
                <Label htmlFor="phone">Téléphone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="06 XX XX XX XX"
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Adresse</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="address">Adresse</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="123 rue Example"
                />
              </div>
              <div>
                <Label htmlFor="postal_code">Code postal</Label>
                <Input
                  id="postal_code"
                  value={formData.postal_code}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                  placeholder="69000"
                />
              </div>
              <div>
                <Label htmlFor="city">Ville</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Lyon"
                />
              </div>
            </div>
          </div>

          {/* Professional Info */}
          {formData.client_type === 'professionnel' && (
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Informations légales</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="siret">SIRET</Label>
                  <Input
                    id="siret"
                    value={formData.siret}
                    onChange={(e) => setFormData({ ...formData, siret: e.target.value })}
                    placeholder="XXX XXX XXX XXXXX"
                  />
                </div>
                <div>
                  <Label htmlFor="tva_number">N° TVA Intracommunautaire</Label>
                  <Input
                    id="tva_number"
                    value={formData.tva_number}
                    onChange={(e) => setFormData({ ...formData, tva_number: e.target.value })}
                    placeholder="FRXXXXXXXXXXX"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Notes</h2>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Notes internes sur ce client..."
              className="w-full border border-slate-200 rounded-lg px-4 py-2 min-h-[100px]"
            />
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/invoices')}>
              Annuler
            </Button>
            <Button type="submit" disabled={loading}>
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Enregistrement...' : isEdit ? 'Mettre à jour' : 'Créer le client'}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
